import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RolePermissionService } from '../../../core/services/role-permission.service';
import { RolePermissionActions } from './role-permission.actions';
import { catchError, finalize, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { AlertService } from 'src/app/core/services/alert.service';
import { Store } from '@ngrx/store';
import { getRolesQueries } from './role-permission.selectors';
import { RolePermissionResponse, SystemRolePermission } from '../../models/rolePermission.model';
import { Router } from '@angular/router';

// Destructure actions for easier usage with ofType
const {
  loadRolePermission,
  loadRolePermissionSuccess,
  loadRolePermissionFailure,
} = RolePermissionActions;

@Injectable()
export class RolePermissionEffects {

  constructor(
    private readonly actions$: Actions,
    private readonly rolePermissionService: RolePermissionService,
    private readonly alertService: AlertService,
    private readonly router: Router,
    private readonly store: Store
  ) {}

  loadRolePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRolePermission),
      mergeMap(({ roleId }) =>
        from(this.rolePermissionService.getRolePermission(roleId)).pipe(
          map((response: RolePermissionResponse) =>
            loadRolePermissionSuccess({ rolePermission: response.data })
          ),
          catchError((error) =>
            of(loadRolePermissionFailure({ error }))
          )
        )
      )
    )
  );

  // Create Role Effect
  createRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolePermissionActions.createRolePermission),
      mergeMap(({ payload }) =>
        from(this.rolePermissionService.createRole(payload)).pipe(
          map((response) => {
            this.alertService.success(response.message, 'Success');
            return RolePermissionActions.createRolePermissionSuccess({
              role: response.data,
            })
          }),
          catchError((error) =>
            of(RolePermissionActions.createRolePermissionFailure({ error }))
          )
        )
      )
    )
  );

  redirectAfterRoleCreate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          RolePermissionActions.createRolePermissionSuccess
        ),
        tap(() => this.router.navigate(['/roles'])),
      ),
    { dispatch: false } // ✅ navigation side-effect only
  );


  refreshDataOnChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        RolePermissionActions.updateRolePermissionSuccess
      ),
      mergeMap(() =>
        of(
          RolePermissionActions.listRolePermissions()
      )
    )
  ));
  // Refresh after create
  // refreshAfterCreate$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(RolePermissionActions.createRolePermissionSuccess),
  //     map(() => RolePermissionActions.loadRolePermission({ roleId: 0 })) // Assuming roleId is 0 for refresh, adjust as needed
  //   )
  // );

  loadRolePermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolePermissionActions.listRolePermissions),
      withLatestFrom(this.store.select(getRolesQueries)),
      mergeMap(([, filters]) =>
        from(this.rolePermissionService.listRoles(
          filters.body ?? {},
          filters.params.page,
          filters.params.limit
        )).pipe(
          map((response) =>
            RolePermissionActions.listRolePermissionsSuccess({ response })
          ),
          catchError((error) =>
            of(RolePermissionActions.listRolePermissionsFailure({
              error: error.message || 'Failed to load roles'
            }))
          )
        )
      )
    )
  );

  roleNameList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolePermissionActions.roleNameList),
      mergeMap(() =>
        from(this.rolePermissionService.roleNames()).pipe(
          map((response) =>
            RolePermissionActions.roleNameListSuccess({ response })
          ),
          catchError((error) =>
            of(RolePermissionActions.roleNameListFailure({
              error: error.message || 'Failed to load roles'
            }))
          )
        )
      )
    )
  );

  allModulePermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolePermissionActions.allModulePermissions),
      mergeMap(() =>
        from(this.rolePermissionService.allModulePermissions()).pipe(
          map((response) =>
            RolePermissionActions.allModulePermissionsSuccess({ response })
          ),
          catchError((error) =>
            of(RolePermissionActions.allModulePermissionsFailure({
              error: error.message || 'Failed to load roles'
            }))
          )
        )
      )
    )
  );

  // System role permissions
  systemRolePermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolePermissionActions.listSystemRolePermissions),
      mergeMap(() =>
        from(this.rolePermissionService.getSystemRolePermissions()).pipe(
          map((response: SystemRolePermission) =>
            RolePermissionActions.listSystemRolePermissionsSuccess({
              response: response,
            })
          ),
          catchError((error) =>
            of(RolePermissionActions.listSystemRolePermissionsFailure({
              error: error.message || 'Failed to load system role permissions'
            }))
          )
        )
      )
    )
  );

  selectCurrentRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolePermissionActions.selectCurrentRolePermission),
      mergeMap(({ id }) =>
        from(this.rolePermissionService.getRoleById(id)).pipe(
          map((role) =>
            RolePermissionActions.selectCurrentRolePermissionSuccess({ role : role.data })
          ),
          catchError((error) =>
            of(
              RolePermissionActions.selectCurrentRolePermissionFailure({ error: error.message })
            )
          )
        )
      )
    )
  );

  updateRolePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolePermissionActions.updateRolePermission),
      mergeMap((action) =>
        from(this.rolePermissionService.updateRole(action.params)).pipe(
          map((response) => {
            this.alertService.success(response.message, 'Success');
            action.closeModalSignal.set(true);
            return RolePermissionActions.updateRolePermissionSuccess({
              role: response.data
            })
          }),
          catchError((error) =>
            of(RolePermissionActions.updateRolePermissionFailure({
              error: error.message || 'Failed to update role'
            }))
          )
        )
      )
    )
  );

  deleteRolePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolePermissionActions.deleteRolePermission),
      mergeMap((action) =>
        from(this.rolePermissionService.deleteRole(action.id)).pipe(
          map((response) => {
            this.alertService.success(response.message, 'Success');
            action.closeModalSignal.set(true);
            return RolePermissionActions.deleteRolePermissionSuccess({
              id: action.id
            })
          }),
          catchError((error) =>
            of(RolePermissionActions.deleteRolePermissionFailure({
              error: error.message || 'Failed to delete role'
            }))
          ),
          finalize(() => {
          action.closeModalSignal.set(true);
        })
        )
      )
    )
  );

  assignedRoleCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RolePermissionActions.checkAssignedRoleCount),
      mergeMap(({ id }) =>
        from(this.rolePermissionService.assignedRoleCount(id)).pipe(
          map((response) =>
            RolePermissionActions.checkAssignedRoleCountSuccess({ response: response })
          ),
          catchError((error) =>
            of(
              RolePermissionActions.checkAssignedRoleCountFailure({ error: error.message })
            )
          )
        )
      )
    )
  );

}
